// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Drawer } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { type JSX, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { dispatchPatientCreated } from '../../../utils/patient-updates';
import { AddPatientModal } from '../AddPatientModal';
import classes from './PatientListLayout.module.css';
import type { PatientListOutletContext } from './PatientListLayoutContext';
import { PatientListSidebar } from './PatientListSidebar';

export function PatientListLayout(): JSX.Element {
  const isMobile = useMediaQuery('(max-width: 950px)') ?? false;
  const [opened, { open, close }] = useDisclosure(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [addPatientOpened, setAddPatientOpened] = useState(false);
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (isMobile && !patientId) {
      open();
    } else {
      close();
    }
  }, [isMobile, patientId, open, close]);

  const outletContext: PatientListOutletContext = { openSwitchPatient: open, isMobile };

  return (
    <div className={classes.container}>
      {!isMobile && (
        <div className={`${classes.sidebar} ${sidebarCollapsed ? classes.sidebarCollapsed : ''}`}>
          <PatientListSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
            onAddPatient={() => setAddPatientOpened(true)}
          />
        </div>
      )}
      <div className={classes.content}>
        <Outlet context={outletContext} />
      </div>
      {isMobile && (
        <Drawer
          opened={opened}
          onClose={close}
          size="100%"
          position="left"
          padding={0}
          title={patientId ? 'Switch Patient' : 'Select Patient'}
          withCloseButton={Boolean(patientId)}
          closeOnClickOutside={Boolean(patientId)}
          closeOnEscape={Boolean(patientId)}
          classNames={{ body: classes.drawerBody, header: classes.drawerHeader }}
        >
          <PatientListSidebar onAddPatient={() => setAddPatientOpened(true)} />
        </Drawer>
      )}
      <AddPatientModal
        opened={addPatientOpened}
        onClose={() => setAddPatientOpened(false)}
        onCreated={(patient) => {
          setAddPatientOpened(false);
          dispatchPatientCreated(patient);
          if (patient.id) {
            Promise.resolve(navigate(`/Patient/${patient.id}`)).catch(console.error);
          }
        }}
      />
    </div>
  );
}
